import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Award, TrendingUp, FileText, Building2, RefreshCw } from 'lucide-react'

const BADGE_CONFIG = {
  gold:   { label: 'Gold',   ring: 'ring-2 ring-accent', bg: 'bg-warning/15', text: 'text-warning', dot: 'bg-accent' },
  silver: { label: 'Silver', ring: 'ring-2 ring-[hsl(var(--muted-foreground))]', bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-[hsl(var(--muted-foreground))]' },
  bronze: { label: 'Bronze', ring: 'ring-2 ring-[hsl(var(--warning))]/40', bg: 'bg-warning/15', text: 'text-warning', dot: 'bg-[hsl(var(--warning))]' },
  none:   { label: 'Unranked', ring: 'ring-1 ring-border', bg: 'bg-muted/30', text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
}

const NEXT_TIER = { none: { label: 'Bronze', threshold: 100 }, bronze: { label: 'Silver', threshold: 401 }, silver: { label: 'Gold', threshold: 701 }, gold: null }

const SCORE_BARS = [
  { key: 'score_volume',       label: 'Volume',       max: 150 },
  { key: 'score_reach',        label: 'Reach',        max: 250 },
  { key: 'score_breadth',      label: 'Breadth',      max: 150 },
  { key: 'score_recency',      label: 'Recency',      max: 100 },
  { key: 'score_accreditation',label: 'Accreditation',max: 100 },
]

export function JournalistScoreCard() {
  const { data: score, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['journalist-score'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_coverage_scores' as any)
        .select('*')
        .eq('journalist_id', user.id)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  if (isLoading) {
    return (
      <div className="bg-background border border-border rounded-xl p-5 animate-pulse">
        <div className="h-4 w-28 bg-muted rounded mb-3" />
        <div className="h-12 w-20 bg-muted rounded mb-2" />
        <div className="h-3 w-40 bg-muted rounded" />
      </div>
    )
  }

  if (!score) {
    return (
      <div className="bg-background border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Award size={15} className="text-muted-foreground" />
          <span className="text-[13px] font-semibold">Coverage Score</span>
        </div>
        <div className="text-[28px] font-bold text-muted-foreground/30"></div>
        <p className="text-justify text-[11.5px] text-muted-foreground mt-1">
          Submit your first article to start building your score.
        </p>
      </div>
    )
  }

  const tier = (score.badge_tier as keyof typeof BADGE_CONFIG) ?? 'none'
  const badge = BADGE_CONFIG[tier]
  const next = NEXT_TIER[tier as keyof typeof NEXT_TIER]
  const total = Math.round(score.score_total ?? 0)
  const progress = next ? Math.min((total / next.threshold) * 100, 100) : 100

  return (
    <div className={`bg-background border border-border rounded-xl p-5 ${badge.ring}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Award size={15} className={badge.text} />
            <span className="text-[13px] font-semibold">Coverage Score</span>
          </div>
          <div className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
            {badge.label}
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh score"
        >
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="text-[40px] font-bold leading-none mb-1">{total}</div>
      <div className="text-[11px] text-muted-foreground mb-4">out of 1,000 points</div>

      {next && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
            <span>{total} pts</span>
            <span>{next.label} at {next.threshold}</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-success rounded-full transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {tier === 'gold' && (
        <div className="mb-4 text-[11.5px] text-accent font-medium">
          Maximum tier achieved.
        </div>
      )}

      {/* Score breakdown */}
      <div className="space-y-2">
        {SCORE_BARS.map(bar => {
          const val = Math.round(score[bar.key] ?? 0)
          const pct = Math.min((val / bar.max) * 100, 100)
          return (
            <div key={bar.key}>
              <div className="flex justify-between text-[10.5px] text-muted-foreground mb-0.5">
                <span>{bar.label}</span>
                <span>{val} / {bar.max}</span>
              </div>
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary/60 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <FileText size={11} />
          {score.article_count ?? 0} articles
        </span>
        <span className="flex items-center gap-1">
          <Building2 size={11} />
          {score.org_count ?? 0} institutions
        </span>
        {score.last_computed_at && (
          <span className="flex items-center gap-1 ml-auto">
            <TrendingUp size={11} />
            {new Date(score.last_computed_at).toLocaleDateString('en', { day: 'numeric', month: 'short' })}
          </span>
        )}
      </div>
    </div>
  )
}
